import { Component, OnInit, Input, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DxDataGridModule, DxLoadPanelModule, DxButtonModule } from 'devextreme-angular';
import { CrudService } from '../services/crud.service';
import { NotifyService } from '../services/notify.service';
import { ConfirmationService } from '../services/confirmation.service';
import { environment } from 'src/environments/environment';
import CustomStore from 'devextreme/data/custom_store';
import { confirm } from 'devextreme/ui/dialog';
import { firstValueFrom, Observable, map } from 'rxjs';
import { RebateNames } from 'src/app/models/rebate-names.model';
import { PromotionGroupItemsComponent } from '../settings/promotion-group-items/promotion-group-items.component';

@Component({
  selector: 'app-negotiation',
  imports: [CommonModule, DxDataGridModule, DxLoadPanelModule, DxButtonModule, PromotionGroupItemsComponent],
  templateUrl: './negotiation.component.html',
  styleUrl: './negotiation.component.css',
  standalone: true
})
export class NegotiationComponent implements OnInit, OnChanges {
  
  @Input() promotion_id: number = 0;
  @Input() promotion_name: string = '';
  dataSource: any;
  loadingVisible: boolean = false;
  rebateNames$: Observable<RebateNames[]>;
  currencyFormat = { type: 'currency', precision: 2 };
  private apiUrl: string = environment.baseApiUrl;

  constructor(
    private crudService: CrudService,
    private notify: NotifyService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.initializeDataSource();
  }

  private initializeDataSource(): void {
    this.dataSource = new CustomStore({
      key: 'promo_group_id',
      load: async(loadOptions: any) => {
        this.loadingVisible = true;

        let apiEndpoint = `${this.apiUrl}planning-data`;
        
        // Add promotion_id as query parameter if available
        if (this.promotion_id && this.promotion_id > 0) {
          apiEndpoint += `/${this.promotion_id}`;
        }

        return await firstValueFrom(this.crudService.getData(apiEndpoint, loadOptions))
          .then((response: any) => {
            this.rebateNames$ = this.crudService.newGetData(`${this.apiUrl}rebates?type=0`).pipe(map((data: any) => data.data));
            this.loadingVisible = false;
            
            // Handle the API response structure with success and data properties
            if (response && response.success && response.data) {
              return {
                data: response.data,
                totalCount: Array.isArray(response.data) ? response.data.length : 0
              };
            } else {
              // Fallback for different response structures
              const data = response?.data || response || [];
              return {
                data: Array.isArray(data) ? data : [],
                totalCount: Array.isArray(data) ? data.length : 0
              };
            }
          })
          .catch((error: any) => {
            this.loadingVisible = false;
            this.handleError('Failed to load negotiation data', error);
            throw error;
          });
      },
      
      insert: (values: any) => {
        return this.validateData(values)
          .then(() => {
            return firstValueFrom(this.crudService.insertData(`${this.apiUrl}negotiations`, values))
              .then((response: any) => {
                this.notify.tellUser({ success: true, message: 'Negotiation record created successfully' });
                return response;
              });
          })
          .catch((error: any) => {
            this.handleError('Failed to create negotiation record', error);
            throw error;
          });
      },
      
      update: (key: any, values: any) => {
        return this.validateData(values)
          .then(() => {
            return firstValueFrom(this.crudService.updateData(`${this.apiUrl}negotiations`, key, values))
              .then((response: any) => {
                this.notify.tellUser({ success: true, message: 'Negotiation record updated successfully' });
                return response;
              });
          })
          .catch((error: any) => {
            this.handleError('Failed to update negotiation record', error);
            throw error;
          });
      },
      
      remove: (key: any) => {
        return this.confirmDeletion()
          .then(() => {
            return firstValueFrom(this.crudService.deleteData(`${this.apiUrl}negotiations`, key))
              .then(() => {
                this.notify.tellUser({ success: true, message: 'Negotiation record deleted successfully' });
              });
          })
          .catch((error: any) => {
            if (error.message !== 'Deletion cancelled') {
              this.handleError('Failed to delete negotiation record', error);
            }
            throw error;
          });
      }
    });
  }

  private validateData(data: any): Promise<any> {
    return new Promise((resolve, reject) => {
      // Add your validation logic here
      if (!data) {
        reject(new Error('Data is required'));
        return;
      }
      
      // Example validations - adjust according to your data structure
      if (data.promotion_group_name && data.promotion_group_name.trim() === '') {
        reject(new Error('Promotion Group Name cannot be empty'));
        return;
      }
      
      if (data.sell_price && (isNaN(data.sell_price) || data.sell_price < 0)) {
        reject(new Error('Sell price must be a valid positive number'));
        return;
      }
      
      if (data.allowance && (isNaN(data.allowance) || data.allowance < 0)) {
        reject(new Error('Allowance must be a valid positive number'));
        return;
      }
      
      resolve(data);
    });
  }

  private confirmDeletion(): Promise<boolean> {
    return confirm('Are you sure you want to delete this negotiation record? This action cannot be undone.', 'Delete Confirmation')
      .then((result: boolean) => {
        if (result) {
          return true;
        } else {
          throw new Error('Deletion cancelled');
        }
      });
  }

  private handleError(message: string, error: any): void {
    console.error('Negotiation component error:', error);
    
    let errorMessage = message;
    
    if (error?.error?.message) {
      errorMessage = `${message}: ${error.error.message}`;
    } else if (error?.message && error.message !== 'Deletion cancelled') {
      errorMessage = `${message}: ${error.message}`;
    }
    
    this.notify.returnError(errorMessage);
  }

  onEditingStart(e: any): void {
    // Handle any logic when editing starts
    console.log('Editing started for:', e.data);
  }

  onInitNewRow(e: any): void {
    // Initialize default values for new rows
    e.data = {
      promotion_group_name: '',
      sell_price: 0.00,
      allowance: 0.00,
      slot_type_name: 'Shelf',
      // Add other default values as needed
    };
  }

  onRowValidating(e: any): void {
    // Additional row-level validation
    if (e.newData.promotion_group_name && e.newData.promotion_group_name.length < 3) {
      e.errorText = 'Promotion Group Name must be at least 3 characters long';
      e.isValid = false;
    }
    
    if (e.newData.sell_price && e.newData.sell_price <= 0) {
      e.errorText = 'Sell price must be greater than 0';
      e.isValid = false;
    }
  }

  refreshData(): void {
    console.log('refreshData called');
    if (this.dataSource && this.dataSource.reload) {
      this.dataSource.reload();
    } else {
      this.initializeDataSource();
    }
  }

  loadNegotiationData(): void {
    // Reinitialize the data source to trigger a fresh load
    this.initializeDataSource();
    
    // Alternatively, if the dataSource is already initialized, just reload
    if (this.dataSource && this.dataSource.reload) {
      this.dataSource.reload();
    }
  }

  ngOnChanges(): void {
    // This will be called when Input properties change
    if (this.promotion_id && this.promotion_id > 0) {
      this.loadNegotiationData();
    }
  }
}
